import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Button, Card, Input, OTPInput } from "@nutrayan/ui";
import { emailSchema, passwordSchema } from "@nutrayan/utils";
import { apiClient } from "@/lib/api-client";
import { useAuth } from "@/lib/auth";

const loginSchema = z.object({ email: emailSchema, password: passwordSchema });
type LoginForm = z.infer<typeof loginSchema>;

// Contract: POST /api/v1/auth/admin/login → AdminLoginResult; MFA step returns TokenPair.
interface TokenPair {
  accessToken?: string;
  refreshToken?: string;
}

interface AdminLoginResult {
  state?: "MFA_CHALLENGE" | "ENROLLMENT_REQUIRED" | "AUTHENTICATED";
  mfaToken?: string;
  otpAuthUri?: string;
  tokens?: TokenPair;
}

export default function LoginPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const [mfaToken, setMfaTokenLocal] = useState<string | null>(null);
  const [mfaMode, setMfaMode] = useState<"verify" | "enroll">("verify");
  const [otpAuthUri, setOtpAuthUri] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState("");

  const form = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  const finishLogin = (tokens: TokenPair | undefined, email: string) => {
    if (!tokens?.accessToken) {
      toast.error("Sign-in failed — no token issued");
      return;
    }
    auth.setToken(tokens.accessToken, email);
    toast.success("Welcome back");
    navigate("/dashboard");
  };

  const loginMutation = useMutation({
    mutationFn: (data: LoginForm) =>
      apiClient.request<AdminLoginResult>("/api/v1/auth/admin/login", {
        method: "POST",
        body: data,
      }),
    onSuccess: (res, vars) => {
      switch (res.state) {
        case "AUTHENTICATED":
          finishLogin(res.tokens, vars.email);
          break;
        case "MFA_CHALLENGE":
          if (res.mfaToken) {
            setMfaTokenLocal(res.mfaToken);
            setMfaMode("verify");
            toast("Enter your authenticator code");
          } else {
            toast.error("MFA challenge missing token — try again");
          }
          break;
        case "ENROLLMENT_REQUIRED":
          if (res.mfaToken) {
            setMfaTokenLocal(res.mfaToken);
            setMfaMode("enroll");
            setOtpAuthUri(res.otpAuthUri ?? null);
            toast("Scan the QR in your authenticator, then enter the first code");
          } else {
            toast.error("MFA enrollment missing token — try again");
          }
          break;
        default:
          toast.error("Unexpected login response");
      }
    },
    onError: (err: Error) => toast.error(err.message || "Invalid credentials"),
  });

  const mfaMutation = useMutation({
    mutationFn: (totpCode: string) =>
      apiClient.request<TokenPair>(`/api/v1/auth/admin/mfa/${mfaMode}`, {
        method: "POST",
        body: { mfaToken, totpCode },
      }),
    onSuccess: (tokens) => finishLogin(tokens, form.getValues("email")),
    onError: (err: Error) => toast.error(err.message || "Invalid code"),
  });

  return (
    <div className="bg-sand flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <h1 className="font-display text-display-lg text-charcoal">Admin sign in</h1>

        {!mfaToken ? (
          <form
            className="mt-6 space-y-4"
            onSubmit={form.handleSubmit((data) => loginMutation.mutate(data))}
          >
            <div>
              <label className="text-body-sm text-ink" htmlFor="email">
                Email
              </label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                {...form.register("email")}
                invalid={!!form.formState.errors.email}
              />
              {form.formState.errors.email ? (
                <p className="text-caption text-terracotta mt-1">
                  {form.formState.errors.email.message}
                </p>
              ) : null}
            </div>
            <div>
              <label className="text-body-sm text-ink" htmlFor="password">
                Password
              </label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                {...form.register("password")}
                invalid={!!form.formState.errors.password}
              />
              {form.formState.errors.password ? (
                <p className="text-caption text-terracotta mt-1">
                  {form.formState.errors.password.message}
                </p>
              ) : null}
            </div>
            <Button size="lg" fullWidth type="submit" disabled={loginMutation.isPending}>
              {loginMutation.isPending ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        ) : (
          <div className="mt-6 space-y-4">
            {mfaMode === "enroll" ? (
              <div className="bg-sand rounded p-3">
                <p className="text-body-sm text-ink">
                  First login: add this account to your authenticator app, then enter the first
                  6-digit code.
                </p>
                {otpAuthUri ? (
                  <p className="text-caption text-muted mt-2 break-all font-mono">{otpAuthUri}</p>
                ) : null}
              </div>
            ) : (
              <p className="text-body-sm text-ink">
                Enter the 6-digit code from your authenticator.
              </p>
            )}
            <OTPInput
              value={mfaCode}
              onChange={setMfaCode}
              onComplete={(code) => mfaMutation.mutate(code)}
              invalid={mfaMutation.isError}
            />
            <Button
              size="lg"
              fullWidth
              onClick={() => mfaMutation.mutate(mfaCode)}
              disabled={mfaMutation.isPending || mfaCode.length < 6}
            >
              {mfaMutation.isPending
                ? "Verifying…"
                : mfaMode === "enroll"
                  ? "Enroll & sign in"
                  : "Verify"}
            </Button>
            <button
              type="button"
              onClick={() => {
                setMfaTokenLocal(null);
                setMfaCode("");
              }}
              className="text-caption text-forest block w-full text-center underline"
            >
              Back to password
            </button>
          </div>
        )}
      </Card>
    </div>
  );
}
